import React, { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import PropTypes from 'prop-types';
import { FiAlertTriangle, FiCheck, FiInfo, FiX } from 'react-icons/fi';

export const TOAST_KINDS = Object.freeze({
	SUCCESS: 'success',
	ERROR: 'error',
	WARNING: 'warning',
	INFO: 'info',
	LOADING: 'loading',
});

const KIND_CLASS_NAMES = {
	[TOAST_KINDS.SUCCESS]: 'ok',
	[TOAST_KINDS.ERROR]: 'error',
	[TOAST_KINDS.WARNING]: 'warn',
	[TOAST_KINDS.INFO]: 'info',
	[TOAST_KINDS.LOADING]: 'loading',
};

const DEFAULT_DURATIONS = {
	[TOAST_KINDS.SUCCESS]: 4500,
	[TOAST_KINDS.ERROR]: 7000,
	[TOAST_KINDS.WARNING]: 4500,
	[TOAST_KINDS.INFO]: 4500,
	[TOAST_KINDS.LOADING]: 0,
};

const EXIT_DURATION = 160;
const EMPTY_SNAPSHOT = [];
const listeners = new Set();
const autoDismissTimers = new Map();
const removalTimers = new Map();

let nextId = 1;
let items = EMPTY_SNAPSHOT;

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

function normalizeKind(kind, fallback = TOAST_KINDS.INFO) {
	if (kind === 'ok') return TOAST_KINDS.SUCCESS;
	if (kind === 'warn') return TOAST_KINDS.WARNING;
	return Object.values(TOAST_KINDS).includes(kind) ? kind : fallback;
}

function normalizeDuration(duration, kind) {
	if (kind === TOAST_KINDS.LOADING) return 0;
	if (typeof duration !== 'number' || !Number.isFinite(duration) || duration < 0) {
		return DEFAULT_DURATIONS[kind];
	}
	return duration;
}

function notify() {
	listeners.forEach((listener) => listener());
}

function clearAutoDismissTimer(id) {
	const timer = autoDismissTimers.get(id);
	if (timer) clearTimeout(timer);
	autoDismissTimers.delete(id);
}

function clearRemovalTimer(id) {
	const timer = removalTimers.get(id);
	if (timer) clearTimeout(timer);
	removalTimers.delete(id);
}

function remove(id) {
	clearAutoDismissTimer(id);
	clearRemovalTimer(id);
	const nextItems = items.filter((item) => item.id !== id);
	if (nextItems.length === items.length) return;
	items = nextItems;
	notify();
}

function dismiss(id) {
	const item = items.find((candidate) => candidate.id === id);
	if (!item || item.out) return;

	clearAutoDismissTimer(id);
	items = items.map((candidate) => candidate.id === id ? { ...candidate, out: true } : candidate);
	notify();

	removalTimers.set(id, setTimeout(() => remove(id), EXIT_DURATION));
}

function scheduleAutoDismiss(item) {
	clearAutoDismissTimer(item.id);
	if (item.duration > 0 && !item.out) {
		autoDismissTimers.set(item.id, setTimeout(() => dismiss(item.id), item.duration));
	}
}

function createItem(kind, title, options = {}) {
	const normalizedKind = normalizeKind(kind);
	const solid = options.solid === true;
	const duration = normalizeDuration(
		hasOwn(options, 'duration') ? options.duration : DEFAULT_DURATIONS[normalizedKind],
		normalizedKind
	);

	return {
		id: nextId++,
		kind: normalizedKind,
		title,
		desc: solid ? undefined : options.desc,
		action: solid ? undefined : options.action,
		secondary: solid ? undefined : options.secondary,
		duration,
		solid,
		dismissible: solid ? false : options.dismissible !== false,
		out: false,
		revision: 0,
	};
}

function push(kind, title, options = {}) {
	const item = createItem(kind, title, options || {});
	items = [...items, item];
	notify();
	scheduleAutoDismiss(item);
	return item.id;
}

function update(id, patch = {}) {
	const current = items.find((item) => item.id === id);
	if (!current || !patch || typeof patch !== 'object') return false;

	const kindChanged = hasOwn(patch, 'kind');
	const kind = kindChanged ? normalizeKind(patch.kind, current.kind) : current.kind;
	const solid = hasOwn(patch, 'solid') ? patch.solid === true : current.solid;
	let duration = current.duration;

	if (kind === TOAST_KINDS.LOADING) {
		duration = 0;
	} else if (hasOwn(patch, 'duration')) {
		duration = normalizeDuration(patch.duration, kind);
	} else if (kindChanged) {
		duration = DEFAULT_DURATIONS[kind];
	}

	clearAutoDismissTimer(id);
	clearRemovalTimer(id);

	const updated = {
		...current,
		...(hasOwn(patch, 'title') ? { title: patch.title } : {}),
		kind,
		desc: solid ? undefined : hasOwn(patch, 'desc') ? patch.desc : current.desc,
		action: solid ? undefined : hasOwn(patch, 'action') ? patch.action : current.action,
		secondary: solid ? undefined : hasOwn(patch, 'secondary') ? patch.secondary : current.secondary,
		duration,
		solid,
		dismissible: solid
			? false
			: hasOwn(patch, 'dismissible')
				? patch.dismissible !== false
				: current.solid
					? true
					: current.dismissible,
		out: false,
		revision: current.revision + 1,
	};

	items = items.map((item) => item.id === id ? updated : item);
	notify();
	scheduleAutoDismiss(updated);
	return true;
}

function clear() {
	const activeItems = items.filter((item) => !item.out);
	if (!activeItems.length) return;

	activeItems.forEach((item) => clearAutoDismissTimer(item.id));
	items = items.map((item) => item.out ? item : { ...item, out: true });
	notify();

	activeItems.forEach((item) => {
		clearRemovalTimer(item.id);
		removalTimers.set(item.id, setTimeout(() => remove(item.id), EXIT_DURATION));
	});
}

export const toastStore = {
	subscribe(listener) {
		listeners.add(listener);
		return () => listeners.delete(listener);
	},
	getSnapshot() {
		return items;
	},
	push,
	dismiss,
	update,
	clear,
};

export const toast = {
	success: (title, options) => push(TOAST_KINDS.SUCCESS, title, options),
	error: (title, options) => push(TOAST_KINDS.ERROR, title, options),
	warning: (title, options) => push(TOAST_KINDS.WARNING, title, options),
	info: (title, options) => push(TOAST_KINDS.INFO, title, options),
	loading: (title, options) => push(TOAST_KINDS.LOADING, title, options),
	dismiss,
	update,
	clear,
};

function ToastIcon({ kind, solid }) {
	if (kind === TOAST_KINDS.LOADING) return <span className='tst-spin' aria-hidden='true' />;

	const iconProps = {
		size: solid ? 15 : 14,
		strokeWidth: 1.7,
		'aria-hidden': true,
	};

	if (kind === TOAST_KINDS.SUCCESS) return <FiCheck {...iconProps} />;
	if (kind === TOAST_KINDS.INFO) return <FiInfo {...iconProps} />;
	return <FiAlertTriangle {...iconProps} />;
}

ToastIcon.propTypes = {
	kind: PropTypes.oneOf(Object.values(TOAST_KINDS)).isRequired,
	solid: PropTypes.bool.isRequired,
};

export function Toast({ item }) {
	const actionHandled = useRef(false);
	const {
		id,
		kind,
		title,
		desc,
		action,
		secondary,
		duration,
		solid,
		dismissible,
		out,
		revision,
	} = item;

	const handleAction = (callback) => {
		if (actionHandled.current) return;
		actionHandled.current = true;
		try {
			callback?.();
		} finally {
			dismiss(id);
		}
	};

	const className = ['tst', KIND_CLASS_NAMES[kind], solid ? 'solid' : '', out ? 'out' : '']
		.filter(Boolean)
		.join(' ');

	return (
		<div
			className={className}
			role={kind === TOAST_KINDS.ERROR ? 'alert' : 'status'}
			aria-atomic='true'
			style={{ '--tst-duration': `${duration}ms` }}
		>
			<div className='tst-ico'>
				<ToastIcon kind={kind} solid={solid} />
			</div>
			<div className='tst-content'>
				<div className='tst-title'>{title}</div>
				{!solid && desc !== undefined && desc !== null && <div className='tst-desc'>{desc}</div>}
				{!solid && (action || secondary) && (
					<div className='tst-actions'>
						{action && (
							<button type='button' className='tst-action' onClick={() => handleAction(action.onClick)}>
								{action.label}
							</button>
						)}
						{secondary && (
							<button type='button' className='tst-secondary' onClick={() => handleAction(secondary.onClick)}>
								{secondary.label}
							</button>
						)}
					</div>
				)}
			</div>
			{!solid && dismissible && (
				<button type='button' className='tst-x' aria-label='Cerrar notificación' onClick={() => dismiss(id)}>
					<FiX size={14} strokeWidth={1.7} aria-hidden='true' />
				</button>
			)}
			<div key={revision} className={`tst-bar${duration > 0 ? ' timed' : ''}`} />
		</div>
	);
}

Toast.propTypes = {
	item: PropTypes.shape({
		id: PropTypes.number.isRequired,
		kind: PropTypes.oneOf(Object.values(TOAST_KINDS)).isRequired,
		title: PropTypes.node.isRequired,
		desc: PropTypes.node,
		action: PropTypes.shape({ label: PropTypes.string.isRequired, onClick: PropTypes.func }),
		secondary: PropTypes.shape({ label: PropTypes.string.isRequired, onClick: PropTypes.func }),
		duration: PropTypes.number.isRequired,
		solid: PropTypes.bool.isRequired,
		dismissible: PropTypes.bool.isRequired,
		out: PropTypes.bool.isRequired,
		revision: PropTypes.number.isRequired,
	}).isRequired,
};

function ToastStack({ items: stackItems, placement, solidStack = false }) {
	if (!stackItems.length) return null;
	return (
		<div className={`tst-stack ${placement}${solidStack ? ' tst-solid-stack' : ''}`}>
			{stackItems.map((item) => <Toast key={item.id} item={item} />)}
		</div>
	);
}

ToastStack.propTypes = {
	items: PropTypes.arrayOf(Toast.propTypes.item).isRequired,
	placement: PropTypes.oneOf(['br', 'bc', 'tr']).isRequired,
	solidStack: PropTypes.bool,
};

export function ToastHost({ placement = 'br', max = 4 }) {
	const snapshot = useSyncExternalStore(toastStore.subscribe, toastStore.getSnapshot, () => EMPTY_SNAPSHOT);
	const normalizedPlacement = ['br', 'bc', 'tr'].includes(placement) ? placement : 'br';
	const normalizedMax = Number.isFinite(max) && max > 0 ? Math.floor(max) : 4;
	const activeItems = useMemo(() => snapshot.filter((item) => !item.out), [snapshot]);

	useEffect(() => {
		if (activeItems.length > normalizedMax) {
			activeItems.slice(0, activeItems.length - normalizedMax).forEach((item) => dismiss(item.id));
		}
	}, [activeItems, normalizedMax]);

	const visibleItems = snapshot.slice(-normalizedMax).reverse();

	if (normalizedPlacement === 'bc') {
		return <ToastStack items={visibleItems} placement='bc' />;
	}

	return (
		<>
			<ToastStack items={visibleItems.filter((item) => !item.solid)} placement={normalizedPlacement} />
			<ToastStack items={visibleItems.filter((item) => item.solid)} placement='bc' solidStack />
		</>
	);
}

ToastHost.propTypes = {
	placement: PropTypes.oneOf(['br', 'bc', 'tr']),
	max: PropTypes.number,
};
